import { useContext, useState } from 'react';
import { Contract, Recorder } from 'type/type';
import { getContractList, getRecorderList } from 'api/api';
import hubLayout from 'styles/css/hubLayout.module.css';
import { useNavigate } from 'react-router-dom';
import { useModal } from 'components/common/ModalLayout';
import Loading from 'components/common/Loading';
import { styled } from 'styled-components';
import { AgentLoginInfoContext } from 'pages/Layout';
import { contractStatusLabel } from 'pages/utils/commonUtils';

const CommonTableWrap = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: auto;
}`;

const CommonTable = styled.table`
  font-size: 12px;
  border-collapse: collapse;
  border: 0;
  th, td {
    text-align: center;
  }
  thead {
    height: 3rem;
    font-weight: 400;
    color: #fff;
    background-color: #1568ddad;
  }
  tbody {
    tr {
      height: 3rem;
    }
    td {
      padding: 0.25rem;
      border: 1px solid #bfbfbf;
      color: #666;
      &:first-child {
        border-left: 0;
      }
      &:last-child {
        border-right: 0;
      }
      white-space: nowrap;      /* 텍스트가 한 줄로 표시되도록 설정 */
      overflow: hidden;         /* 넘치는 텍스트를 숨김 */
      text-overflow: ellipsis;  /* 넘친 텍스트를 '...'로 표시 */
      max-width: 6rem;
    }
    &:hover {
      cursor: pointer;
    }
  }

  .w-5 {
    width: 5%;
  }
  .w-10 {
    width: 10%;
  }
  .w-15 {
    width: 15%;
  }
  .w-20 {
    width: 20%;
  }
  .w-30 {
    width: 20%;
  }
`;

interface SearchInputType {
  type: string,
  value: string,
  jisa_code: string[],
};


function ContractList() {
  const { agentLoginInfo } = useContext(AgentLoginInfoContext);
  const navigate = useNavigate();
  const { showModal } = useModal();
  const [loading, setLoading] = useState(false);

  const [totalCount, setTotalCount] = useState<number>(-1);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [recorderList, setRecorderList] = useState<Recorder[][]>([]);  // 장비 정보 상태 추가
  const [searchObj, setSearchObj] = useState<SearchInputType>({
    type: "contract_no",
    value: "",
    jisa_code: Array.isArray(agentLoginInfo.jisa_code)
      ? (agentLoginInfo.jisa_code as string[])
      : typeof agentLoginInfo.jisa_code === "string"
      ? [agentLoginInfo.jisa_code] // 문자열이면 배열로 변환
      : [],
  });

  const getContracts = async () => {
    setLoading(true);
    try {
      if (!searchObj.value) {
        return showModal("alert", "검색어를 입력해주세요.");
      }
      const contractRes = await getContractList(0, 0, { [searchObj.type]: searchObj.value, jisa_code: searchObj.jisa_code });
      if (contractRes.code === 200) {
        setContracts(contractRes.data);
        setTotalCount(contractRes.total_count);

        // 각 계약번호에 대해 장비 정보를 가져오는 로직 추가
        const allRecorders = await Promise.all(
          contractRes.data.map(async (contract: Contract) => {
            const recorderRes = await getRecorderList(0, 0, { contract_no: contract.contract_no, is_registe: 'All' });
            return recorderRes.code === 200 ? recorderRes.data : [];
          })
        );
        setRecorderList(allRecorders);
      } else {
        showModal("alert", contractRes.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // 검색 type 변경
  const handleSearchType = (type: string) => {
    setSearchObj(prev => ({...prev, type, value: "" }));
  };
  // 검색 value 변경
  const handleSearchValue = (value: string) => {
    if (searchObj.type === "id") value = value.toLowerCase();
    setSearchObj(prev => ({...prev, value}));
  };

  return (
    <>
      {loading ? <Loading /> :
        <div className={`${hubLayout.cnt_box}`}>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={`${hubLayout.sec_title}`}>
              <h2>고객 계약 관리</h2>
              <span onClick={() => navigate("/agent/menu")}>X</span>
            </div>

            <div className={`${hubLayout.btn_group}`}>
              <p style={{ fontSize: '18px', display: 'flex', alignItems: 'center' }}>
                <strong className={`${hubLayout.tx_blue}`}>계약조회</strong>
              </p>
            </div>
            <ul className={`${hubLayout.inp_list}`}>
              <li className={`${hubLayout.display_flex}`}>
                <div className={`${hubLayout.inp_select}`}>
                  <select name="search_type" className={`${hubLayout.inp_select}`} onChange={(e) => handleSearchType(e.target.value)} value={searchObj.type}>
                    <option value={`contract_no`}>계약번호</option>
                    <option value={`name`}>계약자명</option>
                    <option value={`company_name`}>상호명</option>
                  </select>
                </div>
                <input type="text" name="search_value" onChange={(e) => handleSearchValue(e.target.value)} value={searchObj.value} />
              </li>
            </ul>
            <div className={`${hubLayout.btn_group}`}>
              <button type="button" className={`${hubLayout.btn} ${hubLayout.large} ${hubLayout.blue}`} onClick={getContracts}>조회</button>
            </div>
            {totalCount > -1 &&
              <>
                <p className={`${hubLayout.acc_total}`} style={{ margin: '5px 0 20px' }}>총 <strong className={`${hubLayout.tx_blue}`}>{totalCount}</strong>개</p>
                <CommonTableWrap>
                  <h4>계약정보</h4>
                  <CommonTable>
                    {totalCount === 0 ? (
                      <tbody>
                        <tr>
                          <td colSpan={4} style={{ border: '0', textAlign: 'center' }}>
                            조회된 계약이 없습니다.
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      <>
                        <thead>
                          <tr>
                            <th className='w-5'>계약자명</th>
                            <th className='w-10'>계약번호</th>
                            <th className='w-10'>상호명</th>
                            <th className='w-5'>상태</th>
                          </tr>
                        </thead>
                        <tbody className='contractTbody'>
                          {contracts.map((contract, idx) => (
                            <tr key={`contract_${idx}`}>
                              <td>{contract.name}</td>
                              <td>{contract.contract_no}</td>
                              <td>{contract.company_name}</td>
                              <td>{contractStatusLabel.find(item => item.code === contract.contract_status)?.name || ""}</td>
                            </tr>
                          ))}
                        </tbody>
                        <div style={{ marginTop: '20px' }} />
                        {/* legend 대신 h3 사용 */}
                        <h4>장비정보</h4>
                        <thead>
                          <tr>
                            <th>계약번호</th>
                            <th>MAC</th>
                            <th>설치위치</th>
                            <th>개통상태</th>
                          </tr>
                        </thead>
                        <tbody className='recorderTbody'>
                          {recorderList && recorderList.length > 0 ? (
                            recorderList.map((recorders: Recorder[], idx: number) => (
                              recorders.map((recorder: Recorder, index: number) => (
                                <tr key={`recorder_${idx}_${index}`}>
                                  <td>{recorder.contract_no}</td>
                                  <td>{recorder.mac}</td>
                                  <td>{recorder.reg_name}</td>
                                  <td>{recorder.is_registe ? '개통완료' : '1차개통'}</td>
                                </tr>
                              ))
                            ))
                          ) : (
                            <tr>
                              <td colSpan={4} style={{ textAlign: 'center' }}>장비 정보가 없습니다.</td>
                            </tr>
                          )}
                        </tbody>
                      </>
                    )}
                  </CommonTable>
                </CommonTableWrap>
              </>
            }
          </div>
        </div>
      }
    </>
  );
}

export default ContractList;